/**
 * StudioPropertiesPanel.tsx — Painel de propriedades do bloco selecionado
 *
 * Os campos são gerados a partir do catálogo único de blocos. Além dos campos
 * simples, o painel sabe editar:
 *  - `responsive`: o mesmo campo repetido por tamanho de tela (abas);
 *  - `group`: um conjunto de campos guardado em um único objeto.
 *
 * Campos marcados como avançados ficam escondidos atrás do botão "Avançado",
 * para que a tela continue simples para quem só quer montar a página.
 *
 * RELEASE: v1.2.0 - 2026-09-11 - Edição de conteúdo das seções em HTML livre
 * RELEASE: v1.1.0 - 2026-09-10 - Campos responsivos, campos compostos e modo avançado
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial
 */

import React from 'react';
import { Monitor, Settings2, SlidersHorizontal, Smartphone, Tablet, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  getBlock,
  getComposition,
  STUDIO_BREAKPOINTS,
  type StudioBlockNode,
  type StudioBlockProp,
  type StudioBreakpoint,
} from '@/lib/studio/core';
import { lerCamposDaSecao } from '@/lib/studio/html/section';
import { SectionCard, CompactLabel } from '../workflow/inspector/SectionCard';

interface StudioPropertiesPanelProps {
  node: StudioBlockNode | null;
  onChange: (propKey: string, valor: unknown) => void;
  onClose?: () => void;
}

/** Rótulo e ícone de cada tamanho de tela. */
const TELAS: Record<StudioBreakpoint, { label: string; Icone: typeof Monitor }> = {
  desktop: { label: 'Computador', Icone: Monitor },
  tablet: { label: 'Tablet', Icone: Tablet },
  mobile: { label: 'Celular', Icone: Smartphone },
};

/** Campo simples (sem responsivo e sem composto). */
function CampoSimples({
  prop,
  valor,
  id,
  onChange,
}: {
  prop: Pick<StudioBlockProp, 'type' | 'options'>;
  valor: unknown;
  id: string;
  onChange: (valor: unknown) => void;
}) {
  if (prop.type === 'text' || prop.type === 'image' || prop.type === 'spacing') {
    return (
      <Input
        id={id}
        value={typeof valor === 'string' ? valor : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={prop.type === 'spacing' ? 'ex.: 24px' : undefined}
        className="h-9 bg-background text-xs shadow-sm"
      />
    );
  }

  if (prop.type === 'number') {
    return (
      <Input
        id={id}
        type="number"
        value={typeof valor === 'number' ? valor : ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="h-9 bg-background text-xs shadow-sm"
      />
    );
  }

  if (prop.type === 'color') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="color"
          value={typeof valor === 'string' && valor ? valor : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-11 bg-background p-1 shadow-sm"
        />
        <Input
          value={typeof valor === 'string' ? valor : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="h-9 flex-1 bg-background text-xs shadow-sm"
        />
      </div>
    );
  }

  if (prop.type === 'richtext') {
    return (
      <Textarea
        id={id}
        value={typeof valor === 'string' ? valor : ''}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="min-h-24 resize-y bg-background text-xs shadow-sm"
      />
    );
  }

  if (prop.type === 'select') {
    return (
      <Select
        value={typeof valor === 'string' ? valor : ''}
        onValueChange={(v) => onChange(v)}
      >
        <SelectTrigger id={id} className="h-9 bg-background text-xs shadow-sm">
          <SelectValue placeholder="Selecione" />
        </SelectTrigger>
        <SelectContent>
          {(prop.options ?? []).map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return null;
}

/** Um campo do painel, já ciente de responsivo, composto e sim/não. */
function Campo({
  prop,
  valor,
  idBase,
  onChange,
}: {
  prop: StudioBlockProp;
  valor: unknown;
  idBase: string;
  onChange: (valor: unknown) => void;
}) {
  const id = `${idBase}-${prop.key}`;

  // Sim/não: rótulo e interruptor na mesma linha.
  if (prop.type === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <CompactLabel help={prop.help}>{prop.label}</CompactLabel>
        <Switch id={id} checked={Boolean(valor)} onCheckedChange={(v) => onChange(v)} />
      </div>
    );
  }

  // Conjunto de campos guardado em um único objeto.
  if (prop.type === 'group') {
    const objeto = (valor && typeof valor === 'object' ? valor : {}) as Record<string, unknown>;
    return (
      <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
        <CompactLabel help={prop.help}>{prop.label}</CompactLabel>
        <div className="space-y-2.5">
          {(prop.fields ?? []).map((interno) => (
            <Campo
              key={interno.key}
              prop={interno}
              valor={objeto[interno.key]}
              idBase={id}
              onChange={(v) => onChange({ ...objeto, [interno.key]: v })}
            />
          ))}
        </div>
      </div>
    );
  }

  // Mesmo campo por tamanho de tela.
  if (prop.type === 'responsive') {
    const interno = prop.of;
    const atual =
      valor && typeof valor === 'object' && !Array.isArray(valor)
        ? (valor as Record<string, unknown>)
        : {};

    return (
      <div className="space-y-1.5">
        <CompactLabel help={prop.help ?? 'O celular herda o que não for preenchido.'}>
          {prop.label}
        </CompactLabel>
        <Tabs defaultValue="desktop">
          <TabsList className="h-8 w-full">
            {STUDIO_BREAKPOINTS.map((tela) => {
              const { label, Icone } = TELAS[tela];
              return (
                <TabsTrigger key={tela} value={tela} className="flex-1 gap-1.5 text-[11px]">
                  <Icone className="h-3.5 w-3.5" />
                  {label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {STUDIO_BREAKPOINTS.map((tela) => (
            <TabsContent key={tela} value={tela} className="mt-2">
              <CampoSimples
                prop={{ type: interno?.type ?? 'text', options: interno?.options }}
                valor={atual[tela]}
                id={`${id}-${tela}`}
                onChange={(v) =>
                  onChange({ ...atual, [tela]: v === '' || v === undefined ? undefined : v })
                }
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <CompactLabel help={prop.help}>{prop.label}</CompactLabel>
      <CampoSimples prop={prop} valor={valor} id={id} onChange={onChange} />
    </div>
  );
}

export function StudioPropertiesPanel({ node, onChange, onClose }: StudioPropertiesPanelProps) {
  const definicao = node ? getBlock(node.type) : null;
  const [avancado, setAvancado] = React.useState(false);

  if (!node || !definicao) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6 text-center">
        <p className="text-xs text-muted-foreground">
          Selecione um bloco para ajustar as suas opções.
        </p>
      </div>
    );
  }

  // Nome amigável do espaço semântico ocupado pelo bloco, quando houver.
  const composicao = node.sourceComposition ? getComposition(node.sourceComposition) : undefined;
  const espaco = composicao?.slots.find((item) => item.id === node.slotId);
  const espacoDoBloco = espaco ? `Espaço: ${espaco.label}` : undefined;

  const simples = definicao.props.filter((p) => !p.advanced);
  // Em seções de HTML livre, o conteúdo tem editor próprio (abaixo): as duas
  // propriedades de bastidores não aparecem na lista de avançados.
  const avancados = definicao.props.filter(
    (p) => p.advanced && !(node.type === 'section.html' && (p.key === 'fields' || p.key === 'content')),
  );

  // ------------------------------------------- Conteúdo da seção em HTML livre
  const camposDaSecao = node.type === 'section.html' ? lerCamposDaSecao(node.props?.fields) : [];
  const conteudoDaSecao = (node.props?.content && typeof node.props.content === 'object'
    ? node.props.content
    : {}) as Record<string, unknown>;

  return (
    <div className="flex h-full min-h-0 flex-col bg-card font-body text-card-foreground">
      <div className="flex h-16 shrink-0 items-center gap-3 border-b px-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-studio-accent/10 text-studio-accent">
          <SlidersHorizontal className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-semibold">{definicao.label}</p>
          <p className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">
            {/* Fase 8: quando o bloco veio de uma composição, mostramos o espaço
                que ele ocupa (título, texto, botão...) para orientar a edição. */}
            {espacoDoBloco ?? 'Editar conteúdo e aparência'}
          </p>
        </div>
        {onClose && (
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-3 p-4 pb-8">
          {camposDaSecao.length > 0 && (
            <SectionCard
              title="Conteúdo desta seção"
              help="Troque os textos, links e imagens sem mexer no layout."
            >
              {camposDaSecao.map((campo) => (
                <div key={campo.key} className="space-y-1.5">
                  <CompactLabel>{campo.label}</CompactLabel>
                  {campo.kind === 'text' ? (
                    <Textarea
                      value={String(conteudoDaSecao[campo.key] ?? '')}
                      onChange={(e) =>
                        onChange('content', { ...conteudoDaSecao, [campo.key]: e.target.value })
                      }
                      rows={2}
                      className="min-h-[52px] resize-y bg-background text-xs shadow-sm"
                    />
                  ) : (
                    <Input
                      value={String(conteudoDaSecao[campo.key] ?? '')}
                      onChange={(e) =>
                        onChange('content', { ...conteudoDaSecao, [campo.key]: e.target.value })
                      }
                      placeholder={campo.kind === 'image' ? 'Endereço da imagem' : 'Para onde o link leva'}
                      className="h-9 bg-background text-xs shadow-sm"
                    />
                  )}
                </div>
              ))}
            </SectionCard>
          )}

          <SectionCard
            title="Conteúdo e aparência"
            help="As alterações aparecem imediatamente no documento."
          >
            {definicao.props.length === 0 && (
              <p className="text-[11px] italic text-muted-foreground">
                Este bloco não possui propriedades editáveis.
              </p>
            )}

            {simples.map((prop) => (
              <Campo
                key={prop.key}
                prop={prop}
                valor={node.props?.[prop.key]}
                idBase={`prop-${node.id}`}
                onChange={(v) => onChange(prop.key, v)}
              />
            ))}
          </SectionCard>

          {avancados.length > 0 && (
            <SectionCard
              title="Avançado"
              help="Ajustes finos de disposição, camadas e efeitos."
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start gap-1.5 text-[11px]"
                onClick={() => setAvancado((v) => !v)}
              >
                <Settings2 className="h-3.5 w-3.5" />
                {avancado ? 'Ocultar ajustes avançados' : 'Mostrar ajustes avançados'}
              </Button>
              {avancado ? (
                avancados.map((prop) => (
                  <Campo
                    key={prop.key}
                    prop={prop}
                    valor={node.props?.[prop.key]}
                    idBase={`prop-${node.id}`}
                    onChange={(v) => onChange(prop.key, v)}
                  />
                ))
              ) : (
                <p className="text-[11px] italic text-muted-foreground">
                  {avancados.length} ajuste(s) fino(s) disponível(is).
                </p>
              )}
            </SectionCard>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
